import { gql, useLazyQuery, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';

import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { UserContext, usePermission } from '@providers/userContextProvider';
import useTextDebounce from '@utils/useTextDebounce';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  useTheme,
} from 'hephaestus/web';
import { Close, ExpandLess, ExpandMore } from 'hephaestus/webIcons';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { NewOrganization } from './newOrganization';

const SEARCH_ORGANIZATIONS = gql`
  query searchOrganizations($input: FetchOrganizationsInput!) {
    fetchOrganizations(input: $input) {
      organizations {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        parents {
          id
          translatedName { ${TRANSLATED_STRING_FIELDS} }
        }
      }
    }
  }
`;

const FETCH_INITIAL_ORGANIZATIONS = gql`
  query fetchInitialOrganizations($organizationId: String!) {
    fetchOrganization(organizationId: $organizationId) {
      organization {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        parents {
          id
          translatedName { ${TRANSLATED_STRING_FIELDS} }
          parent {
            id
          }
          children {
            id
            translatedName { ${TRANSLATED_STRING_FIELDS} }
            children {
              id
            }
          }
        }
      }
    }
  }
`;

const FETCH_CHILD_ORGANIZATIONS = gql`
  query fetchChildOrganizations($organizationId: String!) {
    fetchOrganization(organizationId: $organizationId) {
      organization {
        id
        children {
          id
          translatedName { ${TRANSLATED_STRING_FIELDS} }
          children {
            id
          }
        }
      }
    }
  }

`;

export const OrganizationSwitcher = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (e: boolean) => void;
}) => {
  const { activeOrganization, setActiveOrganizationId, userContext } =
    useContext(UserContext);
  const accessiblePages = userContext.role?.accessiblePages ?? [];
  const canWriteOrganizations = usePermission('write:organizations');
  const [searchText, setSearchText] = useState('');
  const [localOrgId, setLocalOrgId] = useState(activeOrganization.id);
  const [tree, setTree] = useState<any>({});
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('orgSettings');
  const debouncedSearchText = useTextDebounce({
    value: searchText,
    delay: 500,
  });
  const { data, loading } = useQuery(SEARCH_ORGANIZATIONS, {
    variables: {
      input: {
        filter: {
          searchText: debouncedSearchText,
        },
        pagination: {
          perPage: 1000,
          sortField: 'name',
        },
      },
    },
    skip: !debouncedSearchText,
  });

  const { data: initialData } = useQuery(FETCH_INITIAL_ORGANIZATIONS, {
    variables: {
      organizationId: activeOrganization.id,
    },
    skip: !activeOrganization.id,
    fetchPolicy: 'no-cache',
  });

  const [fetchChildOrganizations] = useLazyQuery(FETCH_CHILD_ORGANIZATIONS);

  const scrollToActive = () => {
    const element = document.getElementById(activeOrganization.id || '');
    if (element) {
      document
        .getElementById('orgSelector')
        ?.scrollTo(0, element.offsetTop + 60);
    }
  };

  useEffect(() => {
    if (initialData?.fetchOrganization?.organization) {
      const { parents }: { parents: any[] } =
        initialData.fetchOrganization.organization;
      let tempParent: any = null;
      parents.forEach((parent: any) => {
        if (tempParent) {
          const newParent = {
            id: parent.id,
            name: parent.translatedName,
            open: true,
            children: [...parent.children]
              .sort((a: any, b: any) =>
                translateBackend(a.translatedName) <
                translateBackend(b.translatedName)
                  ? -1
                  : 1,
              )
              .map((child: any) => {
                if (child.id === tempParent.id) {
                  return tempParent;
                }
                return {
                  id: child.id,
                  name: child.translatedName,
                  open: false,
                  children: [...child.children]
                    .sort((a: any, b: any) =>
                      translateBackend(a.translatedName) <
                      translateBackend(b.translatedName)
                        ? -1
                        : 1,
                    )
                    .map((grandChild: any) => ({
                      id: grandChild.id,
                      name: grandChild.translatedName,
                      open: false,
                    })),
                };
              }),
          };
          tempParent = newParent;
        } else {
          tempParent = {
            id: parent.id,
            name: parent.translatedName,
            open: true,
            children: [...parent.children]
              .sort((a: any, b: any) =>
                translateBackend(a.translatedName) <
                translateBackend(b.translatedName)
                  ? -1
                  : 1,
              )
              .map((child: any) => ({
                id: child.id,
                name: child.translatedName,
                children: [...child.children]
                  .sort((a: any, b: any) =>
                    translateBackend(a.translatedName) <
                    translateBackend(b.translatedName)
                      ? -1
                      : 1,
                  )
                  .map((grandChild: any) => ({
                    id: grandChild.id,
                    name: grandChild.translatedName,
                  })),
                open: false,
              })),
          };
        }
      });
      setTree(tempParent);
    }
    setTimeout(scrollToActive, 200);
  }, [initialData]);

  useEffect(() => {
    setTimeout(scrollToActive, 200);
  }, [open]);

  const updateTreeOpen = async (id: string) => {
    const updateBranch = async (branch: any) => {
      if (branch.id === id) {
        branch.open = !branch.open;
        if (branch.open) {
          const response = await fetchChildOrganizations({
            variables: {
              organizationId: branch.id,
            },
          });
          const children = [
            ...response.data.fetchOrganization.organization.children,
          ]
            .sort((a: any, b: any) =>
              translateBackend(a.translatedName) <
              translateBackend(b.translatedName)
                ? -1
                : 1,
            )
            .map((child: any) => ({
              id: child.id,
              name: child.translatedName,
              open: false,
              children: [...child.children]
                .sort((a: any, b: any) =>
                  translateBackend(a.translatedName) <
                  translateBackend(b.translatedName)
                    ? -1
                    : 1,
                )
                .map((grandChild: any) => ({
                  id: grandChild.id,
                  open: false,
                })),
            }));
          branch.children = children;
        }
      } else {
        for (const child of branch.children || []) {
          await updateBranch(child);
        }
      }
    };

    const updatedTree = { ...tree };
    await updateBranch(updatedTree);
    setTree(updatedTree);
  };

  const showTreeBranch = (branch: any) => (
    <Box
      key={branch?.id}
      sx={{
        pl: 2,
      }}
    >
      {branch?.name ? (
        <Box
          id={branch.id}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pl: 2,
            pr: 2,

            '&:hover': {
              background: theme.color.background,
            },

            borderRadius: theme.borderRadius.md,
          }}
        >
          <Radio
            value={branch.id}
            label={translateBackend(branch.name)}
            onChange={() => setLocalOrgId(branch.id)}
          />

          {branch.children?.length > 0 && (
            <IconButton
              aria-label='collapse'
              sx={{ color: theme.color.onSurface }}
              onClick={() => {
                updateTreeOpen(branch.id);
              }}
            >
              {branch.open ? (
                <ExpandLess fontSize='inherit' />
              ) : (
                <ExpandMore fontSize='inherit' />
              )}
            </IconButton>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            pt: 1,
            pr: 3,
            pl: 2,
          }}
        >
          <Skeleton width='100%' height='34px' variant='rectangular' />
        </Box>
      )}
      {branch?.open &&
        branch?.children.map((child: any) => showTreeBranch(child))}
    </Box>
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {translateBackend(activeOrganization.translatedName)}
          <IconButton aria-label='close' onClick={() => setOpen(false)}>
            <Close fontSize='inherit' />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{ paddingTop: '16px !important', pb: 2 }}
        id='orgSelector'
      >
        <TextField
          leadingIcon='search'
          value={searchText}
          onChange={(e: any) => setSearchText(e.target.value)}
          fullWidth
        />

        {searchText.length === 0 ? (
          <>
            <RadioGroup value={localOrgId} fullWidth>
              <Stack sx={{ pr: 2, pt: 2, width: '100%' }}>
                {showTreeBranch(tree)}
              </Stack>
            </RadioGroup>
          </>
        ) : (
          <>
            {loading ? (
              <Box sx={{ p: 2 }}>
                <Skeleton
                  width='100%'
                  height='54px'
                  sx={{ mb: 1, borderRadius: theme.borderRadius.md }}
                  variant='rectangular'
                />

                <Skeleton
                  width='100%'
                  height='54px'
                  sx={{ mb: 1, borderRadius: theme.borderRadius.md }}
                  variant='rectangular'
                />

                <Skeleton
                  width='100%'
                  height='54px'
                  sx={{ mb: 1, borderRadius: theme.borderRadius.md }}
                  variant='rectangular'
                />
              </Box>
            ) : (
              <RadioGroup value={localOrgId} fullWidth>
                <Stack
                  sx={{
                    pt: 2,
                    width: '100%',
                  }}
                >
                  {data?.fetchOrganizations?.organizations.map((org: any) => (
                    <Box
                      key={org.id}
                      sx={{
                        pl: 2,
                        pr: 2,

                        '&:hover': {
                          background: theme.color.background,
                        },

                        borderRadius: theme.borderRadius.md,
                      }}
                    >
                      <Radio
                        key={org.id}
                        value={org.id}
                        label={translateBackend(org.translatedName)}
                        onChange={() => setLocalOrgId(org.id)}
                        subLabel={[...org.parents]
                          .filter((x: any) => x.id !== org.id)
                          .reverse()
                          .map((x: any) => translateBackend(x.translatedName))
                          ?.join(' > ')}
                      />
                    </Box>
                  ))}
                </Stack>
              </RadioGroup>
            )}
          </>
        )}
      </DialogContent>
      <DialogFooter>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {location.pathname.includes('/settings') && canWriteOrganizations ? (
            <NewOrganization
              afterCreate={(id: string) => {
                setActiveOrganizationId(id);
                setOpen(false);
                navigate('/settings/general');
              }}
            />
          ) : (
            <Box />
          )}
          <Button
            onClick={() => {
              setActiveOrganizationId(localOrgId);
              setOpen(false);
              if (!location.pathname.includes('settings/')) {
                const firstAccessiblePath =
                  accessiblePages[0].toLocaleLowerCase();
                navigate(`/${firstAccessiblePath}`);
              }
            }}
          >
            {t('changeOrganization')}
          </Button>
        </Box>
      </DialogFooter>
    </Dialog>
  );
};
